import Moment from "moment";
import {
    currencyString,
    translateTimezone,
    capitalizeFirstAndLastName,
} from "@qgiv/core-js";

/**
 * @function formatDeadlineDate
 * @description formats date like `06.25.2023 8:00pm EDT`
 * @param {string} date datetime string
 * @param {object} timezoneSettings p2pSettings settings object timezone
 * @returns {string} string with formatted date and timezone concatenated
 */
export const formatDeadlineDate = (date, timezoneSettings) => {
    const momentDeadlineDate = Moment(date);
    let formattedDeadlineDate = momentDeadlineDate.isValid()
        ? momentDeadlineDate.format("M.D.YYYY h:mma z")
        : "";
    const translatedTimezone = translateTimezone(timezoneSettings);
    formattedDeadlineDate += translatedTimezone;
    return formattedDeadlineDate;
};

/**
 * @function convertSingleOptionTags
 * @description replaces a single options tags and returns string with tag replacement
 * @param {string} option custom CMS text
 * @param {object} pattern RegExp object containing matching patterns
 * @param {object} tagConversions object containing tags and their corresponding replacement value
 * @returns {string} commitment option with converted tags
 */
const convertSingleOptionTags = (option, pattern, tagConversions) => {
    const formattedOption = option
        .replace(pattern, (matched) => tagConversions[matched])
        .replaceAll(" , ", ", ");

    return formattedOption;
};

/**
 * @function convertMultipleOptionsTags
 * @description replaces multiple options tags and returns object which values are options with
 * tag replacement
 * @param {object} option object containing several custom CMS text options
 * @param {object} pattern RegExp object containing matching patterns
 * @param {object} tagConversions object containing tags and their corresponding replacement value
 * @returns {object} commitment options with converted tags
 */
const convertMultipleOptionsTags = (option, pattern, tagConversions) => {
    // iterate through the details provided and check if they contain the keys
    // in tag conversions. If so, replace and add to formattedOptions object
    const formattedOptions = {};
    // Uses the tag names as the regex pattern
    Object.entries(option).forEach(([key, detail]) => {
        formattedOptions[key] =
            detail &&
            detail
                .replace(pattern, (matched) => tagConversions[matched])
                .replaceAll(" , ", ", ");
    });

    return { ...formattedOptions };
};

/**
 * @function commitmentOptionsConverter
 * @description replaces tags and returns string
 * @param {string} option custom CMS text
 * @param {number} commitmentAmount the commitment amount based on selected category
 * @param {string} formattedDeadlineDate Moment formatted deadline date
 * @param {object} currency p2pSettings currency object
 * @param {boolean} isCms flag to indicate we are viewing component in cms
 * @param {string} orgName formSettings orgName string
 * @returns {string|object} commitment option with converted tags
 */
export const commitmentOptionsConverter = (
    option,
    commitmentAmount,
    formattedDeadlineDate,
    currency,
    isCms = false,
    orgName = "",
) => {
    const tagConversions = {
        "%CommitmentAmount%": !isCms
            ? currencyString(String(commitmentAmount), currency)
            : "%CommitmentAmount%",
        "%CommitmentDeadline%": !isCms
            ? formattedDeadlineDate
            : "%CommitmentDeadline%",
        "%OrgName%": orgName,
    };

    const pattern = new RegExp(Object.keys(tagConversions).join("|"), "gi");
    if (typeof option === "string") {
        return convertSingleOptionTags(option, pattern, tagConversions);
    }

    if (typeof option === "object") {
        return convertMultipleOptionsTags(option, pattern, tagConversions);
    }

    return null;
};

/**
 * @function transactionReceiptHasCommitments
 * @description determines if the current receipt object contains registrations with commitments
 * @param {string} registrations registrations object found within receipt after processing transaction
 * @returns {boolean} true/false whether or not receipt data contains commitments
 */
export const transactionReceiptHasCommitments = (registrations) =>
    registrations &&
    registrations.some((registration) => registration.commitmentAmount > 0);

/**
 * @function reduxRegistrationsHasCommitments
 * @description determines if the current registrations object in redux contains registrations with commitments
 * @param {string} registrations redux registrations object
 * @returns {boolean} true/false whether or not registration contains commitments
 */
export const reduxRegistrationsHasCommitments = (registrations) =>
    registrations &&
    registrations.some((registration) => registration.Commitment?.amount > 0);

/**
 * @function getCommitmentAgreementDetails
 * @description assembles labels and flags for use in the commitment agreement
 * @param {Array} registrations registrations object from redux
 * @param {string} commitmentAgreementForPrimary registrations object from redux
 * @param {string} commitmentAgreementForAdditional registrations object from redux
 * @param {string} timezone registrations object from redux
 * @param {object} currency registrations object from redux
 * @param {string} orgName registrations object from redux
 * @param {boolean} isCms flag that indicates if we are viewing component on cms
 * @returns {object} returns commitment agreement settings.
 */
export const getCommitmentAgreementDetails = (
    registrations,
    commitmentAgreementForPrimary,
    commitmentAgreementForAdditional,
    timezone,
    currency,
    orgName,
    isCms = false,
) => {
    let primaryHasCommitment = false;
    let additionalHasCommitment = false;
    let commitmentAgreementSettings = {};
    const additionalRegistrantCommitmentDetails = [];
    const formattedOptions = {};
    registrations.forEach((_registration, index) => {
        // Scoped to registrations with commitments only
        if (
            (_registration.Commitment &&
                _registration.Commitment?.amount > 0) ||
            isCms
        ) {
            const formattedDeadlineDate = formatDeadlineDate(
                _registration.Commitment?.deadline,
                timezone,
            );
            // if index is 0 we know this is the primary registrant
            if (index === 0 || isCms) {
                primaryHasCommitment = true;
                formattedOptions.commitmentAgreementForPrimary =
                    commitmentOptionsConverter(
                        commitmentAgreementForPrimary,
                        _registration?.Commitment?.amount || 0,
                        formattedDeadlineDate,
                        currency,
                        isCms,
                        orgName,
                    );
            }
            // if index is greater than 0 we know this is an additional participant
            if (index > 0) {
                additionalHasCommitment = true;
                formattedOptions.commitmentAgreementForAdditional =
                    commitmentOptionsConverter(
                        commitmentAgreementForAdditional,
                        _registration.Commitment.amount,
                        formattedDeadlineDate,
                        currency,
                        isCms,
                        orgName,
                    );
                // create additional registrants commitment info
                const formattedAdditionalDetails = `${capitalizeFirstAndLastName(
                    _registration.First_Name,
                    _registration.Last_Name,
                )}: $${
                    _registration.Commitment.amount
                } due by ${formattedDeadlineDate}`;
                // store additional registrants commitment info in array
                additionalRegistrantCommitmentDetails.push(
                    formattedAdditionalDetails,
                );
            }
        }
        commitmentAgreementSettings = {
            additionalHasCommitment,
            additionalRegistrantCommitmentDetails,
            formattedOptions,
            primaryHasCommitment,
        };
    });
    return commitmentAgreementSettings;
};

/**
 * @public
 * @function getPaymentDetailsAdditionalText
 * @description Creates the additional text for the payment details section when commitments are present
 * @param {object} textOptions contains each of the possible additional text options for payment details
 * @param {boolean} hasCommitments indicates whether or not the current registrations include commitments
 * @param {number} total current registration total
 * @returns {string} additional text wording based on current registration
 */
export const getPaymentDetailsAdditionalText = (
    textOptions,
    hasCommitments,
    total,
) => {
    let paymentDetailsAdditionalText;
    // default additional text
    if (!hasCommitments) {
        paymentDetailsAdditionalText = textOptions.additionalText;
    }
    // zero dollar registrations and a selected category has a commitment
    if (hasCommitments && total === 0) {
        paymentDetailsAdditionalText =
            textOptions.commitmentAdditionalTextWhenNoTotal;
    }
    // Non $0 registration and a selected category has a commitment
    if (hasCommitments && total > 0) {
        paymentDetailsAdditionalText =
            textOptions.commitmentAdditionalTextWhenTotal;
    }

    return paymentDetailsAdditionalText;
};
